/* @img-migrated */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

import Img from './Img';
import { resolveImg } from '../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Premium media-first sponsored placements.
 *
 * Playback rules (paid placements — must be precise):
 *   • Each slide has its OWN duration (`slide.duration` ∈ {5,8,10,12,15} seconds).
 *     Falls back to ad-level `slide_duration`, then 8s.
 *   • Images auto-advance after their effective duration.
 *   • Videos play to completion (`onEnded`); a long safety-net timer guards
 *     against autoplay-blocked / network-stalled videos.
 *   • The next slide is preloaded (image cache + video metadata) before the
 *     current one ends — no blank frame, no flicker, no layout shift.
 *   • Transitions: fade · crossfade · slide — driven by ad.transition.
 *   • Device targeting: ads with `device_visibility` = "desktop" or "mobile"
 *     are filtered client-side based on viewport width (cutoff 1024 px).
 *   • No nav arrows. Subtle dot progress indicator only.
 */

// Single source of truth for the desktop / mobile cutoff.
const DESKTOP_MIN_WIDTH = 1024;
const isDesktopWidth = () => typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches
  : true; // SSR / test fallback — assume desktop

const matchesDevice = (ad, isDesktop) => {
  const dv = ad?.device_visibility || 'both';
  if (dv === 'both') return true;
  if (dv === 'desktop') return isDesktop;
  if (dv === 'mobile') return !isDesktop;
  return true;
};

export const NewsAdSlot = ({ placement = 'mid', className = '' }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDesktop, setIsDesktop] = useState(isDesktopWidth);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/news/ads`, { params: { placement } });
        if (mounted) setAds(res.data?.ads || []);
      } catch { /* ignore */ }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [placement]);

  // Listen to viewport changes so device-targeted ads appear/disappear when
  // an admin resizes the browser or rotates the device.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`);
    const onChange = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange); // older Safari
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const visibleAds = ads.filter((ad) => matchesDevice(ad, isDesktop));

  if (loading || visibleAds.length === 0) return null;

  return (
    <div className={`space-y-5 ${className}`} data-testid={`news-ad-slot-${placement}`}>
      {visibleAds.map((ad) => (
        <NewsAdCard key={ad.id} ad={ad} placement={placement} />
      ))}
    </div>
  );
};

/* Transition presets — duration in seconds (Framer Motion). 0.4–0.7s spec. */
const TRANSITIONS = {
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit:    { opacity: 0 },
    duration: 0.45,
  },
  crossfade: {
    initial: { opacity: 0, scale: 1.015 },
    animate: { opacity: 1, scale: 1 },
    exit:    { opacity: 0, scale: 1.015 },
    duration: 0.6,
  },
  slide: {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit:    { opacity: 0, x: -20 },
    duration: 0.55,
  },
};

const VIDEO_FAILSAFE_MS = 60_000; // never let a stalled video freeze the ad >60s

/** Compute effective duration in ms for an image slide. */
const imageDurationMs = (slide, ad) => {
  const perSlide = Number(slide?.duration);
  if (Number.isFinite(perSlide) && perSlide > 0) return perSlide * 1000;
  const adLevel = Number(ad?.slide_duration);
  if (Number.isFinite(adLevel) && adLevel > 0) return adLevel * 1000;
  return 8000;
};

const NewsAdCard = ({ ad, placement }) => {
  const [idx, setIdx] = useState(0);
  const trackedRef = useRef(false);
  const advanceTimerRef = useRef(null);
  const failsafeTimerRef = useRef(null);
  const media = ad.media || [];
  const showCarouselUI = ad.carousel && media.length > 1;
  const transitionPreset = TRANSITIONS[ad.transition] || TRANSITIONS.crossfade;

  const current = media[idx] || media[0];
  const next = showCarouselUI ? media[(idx + 1) % media.length] : null;

  // Impression tracking — once per mount.
  useEffect(() => {
    if (trackedRef.current || !ad.id) return;
    trackedRef.current = true;
    axios.post(`${API}/news/ads/${ad.id}/view`).catch(() => {});
  }, [ad.id]);

  const clearTimers = useCallback(() => {
    if (advanceTimerRef.current) { clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
    if (failsafeTimerRef.current) { clearTimeout(failsafeTimerRef.current); failsafeTimerRef.current = null; }
  }, []);

  const advance = useCallback(() => {
    clearTimers();
    setIdx((i) => (i + 1) % media.length);
  }, [clearTimers, media.length]);

  // Schedule the next advance based on slide kind. Images use per-slide
  // duration; videos rely on `onEnded` and only set a long failsafe.
  useEffect(() => {
    if (!showCarouselUI || !current) return undefined;
    clearTimers();
    if (current.kind === 'video') {
      // Video: only a failsafe, no hard advance — onEnded handles the normal path.
      failsafeTimerRef.current = setTimeout(advance, VIDEO_FAILSAFE_MS);
    } else {
      const ms = imageDurationMs(current, ad);
      advanceTimerRef.current = setTimeout(advance, ms);
    }
    return clearTimers;
  }, [showCarouselUI, idx, current, ad, advance, clearTimers]);

  // Preload the *next* slide so the swap is gap-less. For images we warm the
  // browser cache via a hidden <Img variant="generic">. For videos we render a hidden
  // `preload="metadata"` element.
  useEffect(() => {
    if (!next) return undefined;
    const url = resolveImg(next.url);
    if (next.kind === 'image') {
      const img = new Image();
      img.src = url;
    }
    // For video, the hidden <video> below will pick it up.
    return undefined;
  }, [next]);

  const handleSlideClick = () => {
    if (!ad.id) return;
    axios.post(`${API}/news/ads/${ad.id}/click`).catch(() => {});
  };

  if (!current) return null;

  // Per-slide link → ad CTA → null. Null = not clickable.
  const slideLink = (current.link || ad.cta_url || '').trim() || null;

  const containerCls = {
    top:    'rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-[0_2px_18px_rgba(0,0,0,0.06)]',
    mid:    'rounded-xl overflow-hidden bg-white border border-slate-200 shadow-[0_1px_8px_rgba(0,0,0,0.04)]',
    bottom: 'rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-[0_2px_18px_rgba(0,0,0,0.06)]',
  }[placement] || '';

  const aspectCls = {
    top:    'aspect-[1/2]',
    mid:    'aspect-[728/90] sm:aspect-[600/300]',
    bottom: 'aspect-[2/1]',
  }[placement] || 'aspect-video';

  const SlideTag = slideLink ? 'a' : 'div';
  const slideProps = slideLink
    ? { href: slideLink, target: '_blank', rel: 'noopener sponsored', onClick: handleSlideClick }
    : {};
  const hoverCls = slideLink ? 'cursor-pointer group' : 'cursor-default';

  return (
    <div className={`${containerCls} relative group/card`} data-testid={`news-ad-${ad.id}`}>
      <SlideTag
        {...slideProps}
        className={`block relative ${hoverCls}`}
        aria-label={ad.name || ad.company_name || 'Sponsored content'}
      >
        {/* Sponsored chip */}
        <span className="absolute top-3 left-3 z-20 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/85 backdrop-blur-sm text-[9.5px] font-bold uppercase tracking-[0.14em] text-slate-600 border border-slate-200 pointer-events-none">
          Sponsored
        </span>

        <div className={`${aspectCls} bg-slate-50 overflow-hidden relative`}>
          <AnimatePresence mode="sync">
            <motion.div
              key={`${ad.id}-${idx}`}
              initial={transitionPreset.initial}
              animate={transitionPreset.animate}
              exit={transitionPreset.exit}
              transition={{ duration: transitionPreset.duration, ease: [0.4, 0, 0.2, 1] }}
              className="absolute inset-0"
            >
              {current.kind === 'video' ? (
                <video
                  key={`v-${ad.id}-${idx}`}
                  src={resolveImg(current.url)}
                  autoPlay
                  muted
                  playsInline
                  preload="auto"
                  loop={!showCarouselUI}
                  onEnded={() => { if (showCarouselUI) advance(); }}
                  onError={() => { if (showCarouselUI) advance(); }}
                  className={`w-full h-full object-cover ${slideLink ? 'group-hover:scale-[1.02] transition-transform duration-700' : ''}`}
                />
              ) : (
                <Img variant="generic"
                  src={resolveImg(current.url)}
                  alt={ad.name || ad.company_name || 'Sponsored'}
                  className={`w-full h-full object-cover ${slideLink ? 'group-hover:scale-[1.02] transition-transform duration-700' : ''}`}
                  decoding="async"
                  loading="eager"
                />
              )}
            </motion.div>
          </AnimatePresence>

          {/* Hidden preload for the next slide — eliminates loading gap. */}
          {next && next.kind === 'video' && (
            <video
              key={`preload-${ad.id}-${(idx + 1) % media.length}`}
              src={resolveImg(next.url)}
              preload="metadata"
              muted
              playsInline
              className="hidden"
              aria-hidden="true"
            />
          )}

          {/* Soft overlay fade — only when clickable */}
          {slideLink && (
            <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/35 via-black/0 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-300 pointer-events-none" />
          )}

          {/* CTA pill — bottom-right, only when CTA text + clickable slide */}
          {ad.cta_text && slideLink && (
            <span
              className="absolute bottom-3 right-3 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#002249] text-[11.5px] font-bold shadow-md border border-white/70 backdrop-blur-sm group-hover:bg-[#E63946] group-hover:text-white group-hover:border-[#E63946] transition-all duration-300 pointer-events-none"
              data-testid={`news-ad-cta-${ad.id}`}
            >
              {ad.cta_text}
              <ArrowUpRight className="w-3.5 h-3.5" />
            </span>
          )}

          {/* Subtle dot progress indicator — bottom center, always visible on
              carousels. No left/right arrows per spec. */}
          {showCarouselUI && (
            <div
              className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-1.5 pointer-events-none"
              data-testid={`news-ad-dots-${ad.id}`}
            >
              {media.map((_, i) => (
                <span
                  key={i}
                  className={`block h-1 rounded-full transition-all duration-300 ${i === idx ? 'bg-white w-6' : 'bg-white/55 w-1.5'}`}
                />
              ))}
            </div>
          )}
        </div>
      </SlideTag>
    </div>
  );
};

export default NewsAdSlot;
